import React, { useEffect, useMemo, useState } from 'react';
import ReactApexChart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';
import { Select, SelectItem, Checkbox } from '@nextui-org/react';
import { DateRange } from 'rsuite/DateRangePicker';
import MultiSelectWithActions from '../../common/MultiSelectWithActions';
import { useTranslation } from '../../../../../common/src/hooks/useTranslation';
import {
  getSettlementsEvolutionDataOptimized,
  getAvailableEvolutionFilters,
  type EvolutionFilters,
} from 'common/src/api/settlements';
import {
  SettlementSalesData,
  SettlementMethodology,
  SettlementGroupBy,
  SettlementDateField,
} from '../../../types/settlements';
import { Session } from '@supabase/supabase-js';
import useUserStore from '../../../../../common/src/stores/useUserStore';
import { SalesChartTable, SalesExcelButton } from './SalesChartTable';
import { EmptyState } from '../../ui';

interface CaliberEvolutionChartProps {
  methodology: SettlementMethodology;
  groupBy: SettlementGroupBy;
  dateField: SettlementDateField;
  dateRange: DateRange | null;
  selectedMarkets: string[];
  session: Session | null;
  userProfile: any;
  selectedSpecies: any;
  selectedSeason: string;
  visualization?: 'chart' | 'table';
}

const CaliberEvolutionChart: React.FC<CaliberEvolutionChartProps> = ({
  methodology,
  groupBy,
  dateField,
  dateRange,
  selectedMarkets,
  session,
  userProfile,
  selectedSpecies,
  selectedSeason,
  visualization = 'chart',
}) => {
  const { t } = useTranslation('settlementsComparative');
  const { darkMode } = useUserStore();
  // Data and loading states
  const [data, setData] = useState<SettlementSalesData | null>(null);
  const [currency, setCurrency] = useState<string>('USD');
  const [totalKilos, setTotalKilos] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);

  // Available filters
  const [availableFilters, setAvailableFilters] = useState<EvolutionFilters>({
    varieties: [],
    calibers: [],
    formats: [],
    colors: [],
  });
  const [filtersLoading, setFiltersLoading] = useState(false);

  // Chart states
  const [selectedVariety, setSelectedVariety] = useState('');
  const [selectedFormats, setSelectedFormats] = useState<string[]>([]);
  const [selectedColors, setSelectedColors] = useState<string[]>([]);
  const [selectedCalibers, setSelectedCalibers] = useState<string[]>([]);
  const [showOwnPrices, setShowOwnPrices] = useState(false);

  // Check if user is demo
  const isDemo = !!userProfile?.exporter?.hasDemo;

  // Fetch available filters
  const fetchAvailableFilters = async () => {
    if (
      !session ||
      !userProfile?.exporter_id ||
      !selectedSeason ||
      !selectedSpecies
    ) {
      return;
    }

    if (selectedSpecies.id !== 2) {
      return;
    }

    setFiltersLoading(true);

    const startDate = dateRange
      ? dateRange[0]?.toISOString().split('T')[0]
      : undefined;
    const endDate = dateRange
      ? dateRange[1]?.toISOString().split('T')[0]
      : undefined;

    try {
      const filtersData = await getAvailableEvolutionFilters(
        session,
        selectedSeason,
        selectedSpecies.id,
        startDate,
        endDate,
        dateField,
        selectedMarkets.length > 0 ? selectedMarkets : undefined
      );

      setAvailableFilters(filtersData);
    } catch (err) {
      console.error('Error fetching available filters:', err);
    } finally {
      setFiltersLoading(false);
    }
  };

  // Fetch evolution data
  const fetchEvolutionData = async () => {
    if (
      !session ||
      !userProfile?.exporter_id ||
      !selectedSeason ||
      !selectedSpecies
    ) {
      return;
    }

    if (selectedSpecies.id !== 2) {
      return;
    }

    setLoading(true);

    const startDate = dateRange
      ? dateRange[0]?.toISOString().split('T')[0]
      : undefined;
    const endDate = dateRange
      ? dateRange[1]?.toISOString().split('T')[0]
      : undefined;

    try {
      const evolutionData = await getSettlementsEvolutionDataOptimized(
        session,
        userProfile.exporter_id,
        methodology,
        selectedSeason,
        'caliber', // evolution grouping by caliber
        dateField,
        selectedSpecies.id,
        startDate,
        endDate,
        groupBy,
        selectedMarkets.length > 0 ? selectedMarkets : undefined,
        'none', // separation
        selectedVariety ? [selectedVariety] : undefined,
        undefined, // no caliber filter for caliber chart
        selectedFormats,
        selectedColors
      );

      if (
        evolutionData &&
        typeof evolutionData === 'object' &&
        'data' in evolutionData
      ) {
        // New API response format with currency and total kilos
        setData(evolutionData.data);
        setCurrency(evolutionData.currency || 'USD');
        setTotalKilos(evolutionData.totalKilos || null);
      } else {
        // Fallback for old format
        setData(evolutionData);
        setCurrency('USD');
        setTotalKilos(null);
      }
    } catch (err) {
      console.error('Error fetching evolution data:', err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch filters on mount and when dependencies change
  useEffect(() => {
    fetchAvailableFilters();
  }, [selectedMarkets, dateRange, dateField, selectedSeason, selectedSpecies]);

  // Auto-select first options when available filters change
  useEffect(() => {
    if (availableFilters.varieties.length > 0 && !selectedVariety) {
      if (isDemo) {
        // For demo users, only select LAPINS if available
        const lapinsVariety = availableFilters.varieties.find(
          (v) => v.value.toUpperCase() === 'LAPINS'
        );
        if (lapinsVariety) {
          setSelectedVariety(lapinsVariety.value);
        }
      } else {
        setSelectedVariety(availableFilters.varieties[0].value);
      }
    }
    if (availableFilters.formats.length > 0 && selectedFormats.length === 0) {
      setSelectedFormats(availableFilters.formats.map((f) => f.value));
    }
    if (availableFilters.colors.length > 0 && selectedColors.length === 0) {
      setSelectedColors(availableFilters.colors.map((c) => c.value));
    }
  }, [
    availableFilters,
    selectedVariety,
    selectedFormats,
    selectedColors,
    isDemo,
  ]);

  // Fetch data when filters change
  useEffect(() => {
    if (selectedVariety) {
      fetchEvolutionData();
    }
  }, [
    methodology,
    groupBy,
    selectedMarkets,
    dateRange,
    dateField,
    selectedVariety,
    selectedFormats,
    selectedColors,
  ]);

  const availableCalibers = useMemo(() => {
    if (!data) return [];
    return Object.keys(data as SettlementSalesData).sort();
  }, [data]);

  useEffect(() => {
    if (availableCalibers.length > 0 && selectedCalibers.length === 0) {
      setSelectedCalibers(availableCalibers.slice(0, 4));
    }
  }, [availableCalibers]);

  const processedData = useMemo((): SettlementSalesData | null => {
    if (!data) {
      return null;
    }
    return data as SettlementSalesData;
  }, [data]);

  const prepareEvolutionChartData = (
    chartData: SettlementSalesData | null,
    calibersFilter: string[]
  ) => {
    if (!chartData) {
      return { categories: [], series: [] };
    }

    const calibersToShow =
      calibersFilter.length > 0 ? calibersFilter : Object.keys(chartData);

    const allDateKeys = new Set<string>();
    calibersToShow.forEach((caliber) => {
      if (chartData[caliber]) {
        Object.keys(chartData[caliber]).forEach((dateKey) =>
          allDateKeys.add(dateKey)
        );
      }
    });

    const dateKeys = Array.from(allDateKeys).sort();
    const series: any[] = [];

    calibersToShow.forEach((caliber, index) => {
      const baseColor = `hsl(${
        index * (360 / calibersToShow.length)
      }, 70%, 50%)`;
      const lightColor = `hsl(${
        index * (360 / calibersToShow.length)
      }, 70%, 80%)`;

      const caliberData = chartData[caliber];
      if (!caliberData) return;

      if (showOwnPrices) {
        const ownData: Array<{ x: string; y: number | null }> = [];

        dateKeys.forEach((dateKey) => {
          const dateData = caliberData[dateKey];
          let ownPrice = null;

          if (dateData && selectedVariety && dateData[selectedVariety]) {
            const varietyData = dateData[selectedVariety];
            if (
              varietyData.own &&
              Array.isArray(varietyData.own) &&
              varietyData.own.length > 0 &&
              varietyData.ownKg &&
              varietyData.ownKg > 0
            ) {
              const ownValue = varietyData.own[0]?.value;
              if (ownValue && typeof ownValue === 'number') {
                const totalSales = ownValue * varietyData.ownKg;
                ownPrice = totalSales / varietyData.ownKg;
              }
            }
          }

          ownData.push({ x: dateKey, y: ownPrice });
        });

        series.push({
          name: `${caliber} - ${t('Tu Exportadora')}`,
          data: ownData,
          color: lightColor,
          type: 'line',
          stroke: {
            width: 3,
            dashArray: 0,
          },
        });
      }

      const othersData: Array<{ x: string; y: number | null }> = [];

      dateKeys.forEach((dateKey) => {
        const dateData = caliberData[dateKey];
        let othersPrice = null;

        if (dateData && selectedVariety && dateData[selectedVariety]) {
          const varietyData = dateData[selectedVariety];
          if (
            varietyData.others &&
            varietyData.others.median &&
            varietyData.others.median > 0
          ) {
            othersPrice = varietyData.others.median;
          }
        }

        othersData.push({ x: dateKey, y: othersPrice });
      });

      series.push({
        name: `${caliber} - ${
          methodology === 'by_volume' ? t('Mercado') : t('Otras Exportadoras')
        }`,
        data: othersData,
        color: baseColor,
        type: 'line',
        stroke: {
          width: 2,
          dashArray: 0, // Solid line
        },
      });
    });

    return {
      categories: dateKeys,
      series: series,
    };
  };

  const chartData = useMemo(
    () => prepareEvolutionChartData(processedData, selectedCalibers),
    [processedData, selectedCalibers, availableCalibers, showOwnPrices]
  );

  const chartOptions: ApexOptions = useMemo(
    () => ({
      chart: {
        type: 'line',
        height: 400,
        foreColor: darkMode ? '#A1A1AA' : '#71717A',
        zoom: {
          enabled: true,
        },
        toolbar: {
          show: true,
        },
      },
      stroke: {
        curve: 'smooth',
        width: chartData.series.map((s) => s.stroke?.width || 2),
        dashArray: chartData.series.map((s) => s.stroke?.dashArray || 0),
      },
      xaxis: {
        categories: chartData.categories,
        title: {
          text: groupBy === 'week' ? t('Semana') : t('Día'),
        },
        labels: {
          formatter: (value) => {
            if (typeof value !== 'string') {
              return value;
            }
            if (groupBy === 'week') {
              const parts = value.split('-W');
              return parts.length > 1 ? parts[1] : value;
            } else {
              const parts = value.split('-');
              if (parts.length === 3) {
                return `${parts[2]}/${parts[1]}`;
              }
              return value;
            }
          },
          rotate: -45,
        },
      },
      yaxis: {
        title: {
          text: `${t('Precio')} (${currency})`,
        },
        labels: {
          formatter: (value) => {
            if (!value) return '';
            const symbol = currency === 'RMB' ? '¥' : 'USD';
            return `${value.toFixed(2)} ${symbol}`;
          },
        },
      },
      tooltip: {
        shared: true,
        intersect: false,
        y: {
          formatter: (value) =>
            value ? `$${value.toFixed(2)}/kg` : t('Sin datos'),
        },
      },
      legend: {
        position: 'bottom',
        horizontalAlign: 'center',
      },
      markers: {
        size: 4,
        hover: {
          size: 6,
        },
      },
      grid: {
        show: true,
        borderColor: '#e0e0e0',
      },
      colors: chartData.series.map((s) => s.color),
    }),
    [chartData.categories, chartData.series, groupBy]
  );

  // Show loading state
  if (loading || filtersLoading) {
    return (
      <div className='flex items-center justify-center h-64'>
        <p className='text-default-400'>
          {filtersLoading
            ? t('Cargando filtros...')
            : t('Cargando datos de evolución...')}
        </p>
      </div>
    );
  }

  // Show no data state
  if (!data) {
    return (
      <EmptyState title={t('No hay datos de evolución disponibles')} />
    );
  }

  // Show no variety selected state
  if (!selectedVariety) {
    return (
      <div className='flex items-center justify-center h-64'>
        <p className='text-default-400'>
          {t('Selecciona una variedad para ver el gráfico')}
        </p>
      </div>
    );
  }

  return (
    <div className='w-full'>
      {/* Chart Title */}
      <div className='mb-4'>
        <div className='flex items-start justify-between gap-3'>
          <h3 className='text-lg font-semibold'>
            {t('Evolución de Precios de Venta por Calibre')}
          </h3>
          <SalesExcelButton
            categories={chartData.categories}
            series={chartData.series}
            currency={currency}
            fileName={`precios_evolucion_calibre_${selectedVariety || 'data'}.xlsx`}
            label={t('Exportar a Excel')}
          />
        </div>
        <p className='text-default-500 text-sm'>
          {t('Moneda')}: {currency}
        </p>
        {totalKilos && (
          <p className='text-default-500 text-sm mt-1'>
            {t('Muestra')}: {totalKilos.toLocaleString('es-CL')} kg
          </p>
        )}
      </div>

      {/* Specific Filters Section */}
      <div className='grid grid-cols-1 md:grid-cols-4 gap-4 mb-6'>
        <MultiSelectWithActions
          label={t('Calibres a mostrar')}
          placeholder={t('Selecciona calibres')}
          options={availableCalibers.map((caliber) => ({
            value: caliber,
            label: caliber,
          }))}
          selectedKeys={selectedCalibers}
          onSelectionChange={setSelectedCalibers}
          size='md'
        />

        <Select
          label={isDemo ? t('Variedad (Demo: Solo Lapins)') : t('Variedad')}
          placeholder={
            isDemo
              ? t('Demo: Solo Lapins disponible')
              : t('Selecciona una variedad')
          }
          selectedKeys={
            selectedVariety ? new Set([selectedVariety]) : new Set()
          }
          onSelectionChange={(keys) => {
            if (!isDemo) {
              const selected = Array.from(keys)[0] as string;
              if (selected) setSelectedVariety(selected);
            }
          }}
          size='md'
          isDisabled={isDemo}
        >
          {(isDemo
            ? availableFilters.varieties.filter(
                (v) => v.value.toUpperCase() === 'LAPINS'
              )
            : availableFilters.varieties
          ).map((variety) => (
            <SelectItem
              key={variety.value}
              value={variety.value}
              textValue={variety.value}
            >
              {variety.value}
            </SelectItem>
          ))}
        </Select>

        <MultiSelectWithActions
          label={t('Formato')}
          placeholder={t('Selecciona formatos')}
          options={availableFilters.formats.map((format) => ({
            value: format.value,
            label: format.value,
          }))}
          selectedKeys={selectedFormats}
          onSelectionChange={setSelectedFormats}
          size='md'
        />

        <MultiSelectWithActions
          label={t('Color')}
          placeholder={t('Selecciona colores')}
          options={availableFilters.colors.map((color) => ({
            value: color.value,
            label: color.value,
          }))}
          selectedKeys={selectedColors}
          onSelectionChange={setSelectedColors}
          size='md'
        />
      </div>

      {/* Chart Controls */}
      <div className='flex items-center justify-end mb-6'>
        <Checkbox
          isSelected={showOwnPrices}
          onValueChange={setShowOwnPrices}
          size='sm'
          color='primary'
        >
          {t('Mostrar mis precios')}
        </Checkbox>
      </div>

      <div className=''>
        {visualization === 'table' ? (
          <SalesChartTable
            categories={chartData.categories}
            series={chartData.series}
            currency={currency}
            emptyLabel={t(
              'No hay datos disponibles para los calibres seleccionados'
            )}
          />
        ) : chartData.series.length > 0 ? (
          <ReactApexChart
            options={chartOptions}
            series={chartData.series}
            type='line'
            height={400}
          />
        ) : (
          <div className='flex items-center justify-center h-64'>
            <p className='text-default-400'>
              {t('No hay datos disponibles para los calibres seleccionados')}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default CaliberEvolutionChart;
